import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Quote } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import StoreBadges from "@/components/StoreBadges";
import { useT } from "@/i18n/LanguageContext";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Approved offline fallback (Mobile Integration Contract §6) — a verified
// canonical record, identical to the server's own fallback.
const FALLBACK = {
  quote_text:
    "Men seek retreats for themselves, houses in the country, sea-shores, and mountains; and thou too art wont to desire such things very much. But this is altogether a mark of the most common sort of men, for it is in thy power whenever thou shalt choose to retire into thyself.",
  author: "Marcus Aurelius",
  source_work: "Meditations",
  canonical_reference: "Meditations 4.3",
  translator: "George Long",
};

function localDate() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * FreeQuoteModal — "Your Stoic Reflection": today's canonical Daily
 * Inspiration, fetched from the SAME central API the Virtue Guide app uses.
 * No local quote library, no client-side rotation — one source of truth.
 */
export default function FreeQuoteModal({ open, onClose }) {
  const [entry, setEntry] = useState(null);
  const cardRef = useRef(null);
  const { t } = useT();

  // Fetch today's canonical quote whenever the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/daily-inspiration?date=${localDate()}`);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (!cancelled) {
          setEntry(data);
          trackEvent("free_stoic_quote_open", { quote_id: data.id, day: data.day_number });
        }
      } catch {
        if (!cancelled) setEntry({ ...FALLBACK, fallback: true });
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Lock body scroll + Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div
      data-testid="quote-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
      className="fixed inset-0 z-[100]"
      style={{ animation: "fadeIn 220ms ease-out" }}
    >
      {/* backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close dialog"
        data-testid="quote-modal-backdrop"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClose(); } }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
      />

      {/* center wrapper */}
      <div className="relative z-10 w-full h-full flex items-center justify-center px-4 py-6 md:py-10 pointer-events-none">
        <div
          ref={cardRef}
          className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-ink-elevated border border-gold/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.95)] p-1 pointer-events-auto"
          style={{ animation: "modalIn 360ms cubic-bezier(.2,.7,.2,1) both" }}
        >
          {/* gold halo */}
          <div className="absolute -inset-px bg-gradient-to-br from-gold/30 via-transparent to-gold/10 -z-10 blur-md opacity-50 pointer-events-none" aria-hidden />

          <div className="relative bg-ink p-7 md:p-10">
            {/* close X */}
            <button
              type="button"
              aria-label="Close dialog"
              data-testid="quote-modal-close"
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-fog hover:text-gold transition-colors z-10"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            {/* header */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="h-px w-8 bg-gold" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{t("freeQuote.modalEyebrow")}</span>
              <span className="h-px w-8 bg-gold" />
            </div>
            <h3
              id="quote-modal-title"
              className="font-serif text-2xl md:text-3xl text-bone text-center tracking-tight leading-tight"
            >
              <span className="italic text-gold">{t("freeQuote.modalHeadline1")}</span> {t("freeQuote.modalHeadline2")}
            </h3>

            {/* today's canonical quote */}
            <div className="mt-10" style={{ animation: "quoteIn 600ms cubic-bezier(.2,.7,.2,1) both" }}>
              <div className="relative px-4 md:px-8">
                <Quote
                  size={28}
                  strokeWidth={1.25}
                  color="#C5A059"
                  className="absolute -top-3 left-0 opacity-70"
                  aria-hidden
                />
                {entry ? (
                  <>
                    <blockquote
                      data-testid="quote-text"
                      className="font-serif italic text-2xl md:text-[1.75rem] leading-[1.3] text-bone pl-6"
                    >
                      &ldquo;{entry.quote_text}&rdquo;
                    </blockquote>
                    <p data-testid="quote-author" className="mt-5 pl-6 text-[11px] tracking-[0.25em] uppercase text-gold">
                      — {entry.author}
                    </p>
                    <p data-testid="quote-source" className="mt-1 pl-6 text-[10px] tracking-[0.18em] uppercase text-fog/70">
                      {entry.canonical_reference || entry.source_work}
                    </p>
                  </>
                ) : (
                  <div data-testid="quote-loading" className="pl-6 py-6">
                    <div className="h-4 w-3/4 bg-white/10 mb-3" />
                    <div className="h-4 w-2/3 bg-white/10 mb-3" />
                    <div className="h-3 w-1/3 bg-white/5" />
                  </div>
                )}
              </div>
            </div>

            {/* footer — download the app */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-center text-[11px] tracking-[0.28em] uppercase text-gold mb-2">
                {t("freeQuote.comingSoon")}
              </p>
              <p className="text-center text-fog font-light text-sm leading-relaxed max-w-sm mx-auto mb-6">
                {t("freeQuote.comingSoonBody")}
              </p>
              <StoreBadges align="center" />
            </div>

            {/* tag line */}
            <p className="mt-10 text-center font-serif italic text-fog/70 text-sm">
              {t("freeQuote.footerTag")}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(12px) scale(.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);   }
        }
        @keyframes quoteIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
